import React, { useState } from 'react';
import { X, Mail, Lock, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'signin';
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, initialMode = 'signin' }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await signIn(formData.email, formData.password);
      toast.success('Welcome back!');
      onClose();
      setFormData({ email: '', password: '' });
    } catch (error: any) {
      toast.error(error.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <style jsx>{`
        .auth-modal-backdrop {
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.9), rgba(59, 130, 246, 0.9), rgba(99, 102, 241, 0.9));
          backdrop-filter: blur(10px);
        }
        
        .auth-modal-container {
          background: white;
          border-radius: 24px;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
          position: relative;
          overflow: hidden;
        }
        
        .auth-modal-container::before {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          width: 120px;
          height: 120px;
          background: linear-gradient(135deg, #60a5fa, #a855f7);
          border-radius: 50%;
          opacity: 0.1;
          transform: translate(50px, -50px);
        }
        
        .auth-modal-container::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #f472b6, #fb923c);
          border-radius: 50%;
          opacity: 0.1;
          transform: translate(-40px, 40px);
        }
        
        .gradient-title {
          background: linear-gradient(135deg, #2563eb, #7c3aed);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          font-weight: 800;
          letter-spacing: 2px;
        }
        
        .icon-container {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          transition: all 0.3s ease;
        }
        
        .icon-email {
          background: linear-gradient(135deg, #3b82f6, #6366f1);
        }
        
        .icon-password {
          background: linear-gradient(135deg, #8b5cf6, #ec4899);
        }
        
        .form-input {
          padding-left: 72px !important;
          padding-right: 16px !important;
          padding-top: 16px !important;
          padding-bottom: 16px !important;
          border-radius: 16px !important;
          border: 2px solid transparent !important;
          background: linear-gradient(135deg, #f8fafc, #e0f2fe) !important;
          font-size: 16px !important;
          transition: all 0.3s ease !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
        }
        
        .form-input:focus {
          background: white !important;
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1), 0 4px 12px rgba(0, 0, 0, 0.1) !important;
          outline: none !important;
        }
        
        .form-input-purple:focus {
          border-color: #8b5cf6 !important;
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1), 0 4px 12px rgba(0, 0, 0, 0.1) !important;
        }
        
        .gradient-button {
          background: linear-gradient(135deg, #3b82f6, #8b5cf6, #6366f1) !important;
          border: none !important;
          border-radius: 16px !important;
          padding: 16px 24px !important;
          font-weight: 700 !important;
          font-size: 18px !important;
          color: white !important;
          box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4) !important;
          transition: all 0.3s ease !important;
          position: relative !important;
          overflow: hidden !important;
        }
        
        .gradient-button:hover:not(:disabled) {
          background: linear-gradient(135deg, #2563eb, #7c3aed, #4f46e5) !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 12px 35px rgba(59, 130, 246, 0.5) !important;
          color: white !important;
        }
        
        .gradient-button:active:not(:disabled) {
          transform: scale(0.98) !important;
        }
        
        .gradient-button:disabled {
          opacity: 0.6 !important;
          cursor: not-allowed !important;
          transform: none !important;
        }
        
        .close-button {
          position: absolute;
          top: 20px;
          left: 20px;
          background: none;
          border: none;
          color: #9ca3af;
          transition: all 0.3s ease;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
        }
        
        .close-button:hover {
          color: #ef4444;
          background: rgba(239, 68, 68, 0.1);
        }
        
        .demo-section {
          background: linear-gradient(135deg, #dbeafe, #e0e7ff);
          border: 2px solid #bfdbfe;
          border-radius: 16px;
          position: relative;
          z-index: 10;
        }
        
        .demo-indicator {
          width: 8px;
          height: 8px;
          background: #10b981;
          border-radius: 50%;
          animation: pulse 2s infinite;
        }
        
        .user-icon-bg {
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          border-radius: 16px;
          padding: 12px;
          display: inline-block;
          box-shadow: 0 8px 25px rgba(59, 130, 246, 0.3);
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .loading-spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
      `}</style>

      <div className="auth-modal-backdrop position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 1050 }}>
        <div className="auth-modal-container p-5" style={{ maxWidth: '450px', width: '90%', position: 'relative' }}>
          
          {/* Close Button */}
          <button
            onClick={onClose}
            className="close-button"
          >
            <X size={24} />
          </button>

          {/* Header */}
          <div className="text-center mb-5" style={{ marginTop: '20px', position: 'relative', zIndex: 10 }}>
            <div className="user-icon-bg mb-4">
              <User className="text-white" size={32} />
            </div>
            <h1 className="gradient-title mb-2" style={{ fontSize: '2.5rem' }}>
              LOGIN
            </h1>
            <p className="text-muted fs-5">Welcome to Grad Vikalp</p>
          </div>

          <form onSubmit={handleSubmit} style={{ position: 'relative', zIndex: 10 }}>
            <div className="mb-4">
              <label className="form-label fw-bold text-dark fs-5 mb-3">
                Email Address
              </label>
              <div className="position-relative">
                <div className="icon-container icon-email">
                  <Mail className="text-white" size={20} />
                </div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="form-control form-input"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div className="mb-5">
              <label className="form-label fw-bold text-dark fs-5 mb-3">
                Password
              </label>
              <div className="position-relative">
                <div className="icon-container icon-password">
                  <Lock className="text-white" size={20} />
                </div>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  className="form-control form-input form-input-purple"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn gradient-button w-100"
            >
              {loading ? (
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <div className="loading-spinner"></div>
                  Please wait...
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Demo Credentials */}
          <div className="demo-section p-4 mt-4">
            <div className="d-flex align-items-center gap-2 mb-3">
              <div className="demo-indicator"></div>
              <p className="text-primary fw-bold mb-0">Demo Admin Login:</p>
            </div>
            <div className="vstack gap-2">
              <div className="d-flex align-items-center gap-2">
                <span className="text-primary fw-semibold">Email:</span>
                <code className="bg-white px-3 py-2 rounded-3 border border-primary text-primary fw-bold">contact us</code>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="text-primary fw-semibold">Password:</span>
                <code className="bg-white px-3 py-2 rounded-3 border border-primary text-primary fw-bold">contact us</code>
              </div>
            </div> 
          </div>
        </div>
      </div> 
    </>
  );
};

export default AuthModal;